import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuthContext} from "../services/auth.service";
import useSWR from "swr";
import axios from "axios";
import {API_URL} from "../services/api.service";
import {toast} from "react-hot-toast";
import Button from "../components/_core/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChartBar,
    faDownload,
    faImages,
    faDatabase,
    faTags,
    faCheckCircle,
    faSync,
    faSpinner
} from "@fortawesome/free-solid-svg-icons";

const SummaryCard = ({icon, title, value, subtitle, color = "blue"}) => {
    const colorClasses = {
        blue: "bg-blue-50 border-blue-200 text-blue-700",
        green: "bg-green-50 border-green-200 text-green-700",
        purple: "bg-purple-50 border-purple-200 text-purple-700",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
    };

    return (
        <div className={`rounded-lg border-2 p-6 ${colorClasses[color]}`}>
            <div className="flex items-center gap-4">
                <div className="text-3xl opacity-80">
                    <FontAwesomeIcon icon={icon} />
                </div>
                <div>
                    <p className="text-sm font-medium opacity-80">{title}</p>
                    <p className="text-3xl font-bold">{value?.toLocaleString() ?? '-'}</p>
                    {subtitle && <p className="text-xs opacity-70 mt-1">{subtitle}</p>}
                </div>
            </div>
        </div>
    );
};

const getDateString = () => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD
};

const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h ago`;
    return `${hours}h ${remainingMinutes}m ago`;
};

const exportToCSV = (data, headers, filename) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach(row => {
        const values = headers.map(header => {
            const value = row[header] ?? '';
            const escaped = String(value).replace(/"/g, '""');
            return escaped.includes(',') ? `"${escaped}"` : escaped;
        });
        csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}-${getDateString()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const ReportingPage = () => {
    const {user, token} = useAuthContext();
    const navigate = useNavigate();
    const [selectedDatasetId, setSelectedDatasetId] = useState('');
    const [isClearing, setIsClearing] = useState(false);

    // Fetch summary data (cached on backend)
    const getSummary = () => axios.get(`${API_URL}/reporting/summary`, {
        headers: {"Authorization": `Bearer ${token}`}
    }).then(res => res.data);

    const {data: summaryData, mutate: mutateSummary} = useSWR(
        token ? `${API_URL}/reporting/summary` : null,
        getSummary
    );

    // Fetch dataset detail only when selected
    const getDatasetDetail = () => axios.get(`${API_URL}/reporting/dataset/${selectedDatasetId}`, {
        headers: {"Authorization": `Bearer ${token}`}
    }).then(res => res.data);

    const {data: detailData, isLoading: detailLoading} = useSWR(
        token && selectedDatasetId ? `${API_URL}/reporting/dataset/${selectedDatasetId}` : null,
        getDatasetDetail
    );

    useEffect(() => {
        if (user && user.user_type !== 3) {
            navigate("/datasets");
        }
    }, [user, navigate]);

    useEffect(() => {
        document.title = "GT2 - Reporting";
    }, []);

    const handleClearCache = async () => {
        setIsClearing(true);
        try {
            await axios.post(`${API_URL}/reporting/clear-cache`, {}, {
                headers: {"Authorization": `Bearer ${token}`}
            });
            await mutateSummary();
            toast.success("Cache cleared and data refreshed", {position: "top-center"});
        } catch (err) {
            toast.error("Failed to clear cache", {position: "top-center"});
        } finally {
            setIsClearing(false);
        }
    };

    const summary = summaryData?.summary;
    const datasets = summaryData?.datasets || [];
    const statuses = summaryData?.statuses || [];
    const isCached = summaryData?.cached;
    const cachedAt = summaryData?.cached_at;

    const detail = detailData;
    const statusNames = statuses.map(s => s.name);

    // Export handlers
    const handleExportDatasetsSummary = () => {
        if (!datasets.length) return;
        const headers = ['Dataset ID', 'Dataset Name', 'Images', 'Annotations'];
        const data = datasets.map(d => ({
            'Dataset ID': d.id,
            'Dataset Name': d.name,
            'Images': d.images,
            'Annotations': d.annotations
        }));
        exportToCSV(data, headers, 'datasets-summary');
    };

    const handleExportDetailStatus = () => {
        if (!detail) return;
        const headers = ['Status', 'Count'];
        const data = Object.entries(detail.status_counts || {}).map(([status, count]) => ({
            'Status': status,
            'Count': count
        }));
        exportToCSV(data, headers, `${detail.dataset.id}-status-report`);
    };

    const handleExportDetailClasses = () => {
        if (!detail) return;
        const headers = ['Class', 'Count'];
        const data = Object.entries(detail.class_counts || {}).map(([cls, count]) => ({
            'Class': cls,
            'Count': count
        }));
        exportToCSV(data, headers, `${detail.dataset.id}-class-report`);
    };

    const handleExportDetailObjects = () => {
        if (!detail) return;
        const headers = ['Object ID', 'Object Name', 'Object Type', 'Annotation Count'];
        const data = (detail.object_counts || []).map(obj => ({
            'Object ID': obj.object_id,
            'Object Name': obj.object_name,
            'Object Type': obj.object_type,
            'Annotation Count': obj.count
        }));
        exportToCSV(data, headers, `${detail.dataset.id}-objects-report`);
    };

    return (
        <div className="w-full h-full flex flex-col bg-slate-50">
            {/* Header */}
            <div className="flex justify-between items-center bg-white py-6 px-8 border-b border-slate-200">
                <h2 className="text-4xl font-bold text-slate-800">
                    <FontAwesomeIcon icon={faChartBar} className="mr-3 text-blue-600" />
                    Reporting
                </h2>
                <div className="flex items-center gap-4">
                    {isCached ? (
                        <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded">
                            Cached {formatTimeAgo(cachedAt)}
                        </span>
                    ) : summaryData ? (
                        <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded">
                            Fresh data
                        </span>
                    ) : null}
                    <Button size="small" style="secondary" onClick={handleClearCache} disabled={isClearing}>
                        <FontAwesomeIcon icon={isClearing ? faSpinner : faSync} className={`mr-2 ${isClearing ? 'animate-spin' : ''}`} />
                        Refresh Data
                    </Button>
                </div>
            </div>

            <div className="flex-grow overflow-auto">
                {/* Summary Cards */}
                <div className="px-8 py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SummaryCard
                            icon={faDatabase}
                            title="Total Datasets"
                            value={summary?.total_datasets}
                            color="blue"
                        />
                        <SummaryCard
                            icon={faImages}
                            title="Total Images"
                            value={summary?.total_images}
                            color="green"
                        />
                        <SummaryCard
                            icon={faTags}
                            title="Total Annotations"
                            value={summary?.total_annotations}
                            color="purple"
                        />
                        <SummaryCard
                            icon={faCheckCircle}
                            title="Status Types"
                            value={statuses.length}
                            color="orange"
                        />
                    </div>
                </div>

                {/* Status Distribution Summary */}
                {summary?.status_distribution && Object.keys(summary.status_distribution).length > 0 && (
                    <div className="px-8 pb-6">
                        <div className="bg-white rounded-lg border border-slate-200 p-4">
                            <h3 className="text-sm font-semibold text-slate-600 mb-3">Overall Status Distribution</h3>
                            <div className="flex flex-wrap gap-3">
                                {Object.entries(summary.status_distribution).map(([status, count]) => (
                                    <div key={status} className="flex items-center gap-2 bg-slate-50 rounded-full px-4 py-2">
                                        <span className="text-sm font-medium text-slate-700">{status}:</span>
                                        <span className="text-sm font-bold text-slate-900">{count.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Datasets Overview Table */}
                <div className="px-8 pb-6">
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
                            <h3 className="text-lg font-semibold text-slate-700">Datasets Overview</h3>
                            <Button size="small" onClick={handleExportDatasetsSummary} disabled={!datasets.length}>
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Export CSV
                            </Button>
                        </div>
                        <div className="overflow-x-auto max-h-64">
                            <table className="w-full">
                                <thead className="sticky top-0">
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                        <th className="text-left px-4 py-3 font-semibold text-slate-600 text-sm">Dataset</th>
                                        <th className="text-right px-4 py-3 font-semibold text-slate-600 text-sm">Images</th>
                                        <th className="text-right px-4 py-3 font-semibold text-slate-600 text-sm">Annotations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {datasets.map((d, idx) => (
                                        <tr key={d.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                            <td className="px-4 py-2">
                                                <div className="font-medium text-slate-800">{d.name}</div>
                                                <div className="text-xs text-slate-500">{d.id}</div>
                                            </td>
                                            <td className="text-right px-4 py-2 text-slate-700">{d.images?.toLocaleString()}</td>
                                            <td className="text-right px-4 py-2 text-slate-700">{d.annotations?.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {datasets.length === 0 && (
                                <div className="text-center py-8 text-slate-500">Loading...</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Dataset Detail Section */}
                <div className="px-8 pb-8">
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-4">
                                <h3 className="text-lg font-semibold text-slate-700">Dataset Detail Report</h3>
                                <select
                                    value={selectedDatasetId}
                                    onChange={(e) => setSelectedDatasetId(e.target.value)}
                                    className="border border-slate-300 rounded px-3 py-2 text-sm min-w-80"
                                >
                                    <option value="">Select a dataset...</option>
                                    {datasets.map(d => (
                                        <option key={d.id} value={d.id}>{d.name} ({d.id})</option>
                                    ))}
                                </select>
                                {detailLoading && (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-slate-400" />
                                )}
                            </div>
                        </div>

                        {!selectedDatasetId ? (
                            <div className="p-12 text-center text-slate-500">
                                <FontAwesomeIcon icon={faDatabase} className="text-4xl mb-4 opacity-30" />
                                <p>Select a dataset from the dropdown above to view detailed statistics</p>
                            </div>
                        ) : detailLoading ? (
                            <div className="p-12 text-center text-slate-500">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl mb-4" />
                                <p>Loading dataset details...</p>
                            </div>
                        ) : detail ? (
                            <div className="p-6 space-y-6">
                                {/* Dataset Info Header */}
                                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                                    <div>
                                        <h4 className="text-xl font-bold text-slate-800">{detail.dataset.name}</h4>
                                        <p className="text-sm text-slate-500">{detail.dataset.id}</p>
                                        {detail.dataset.description && (
                                            <p className="text-sm text-slate-600 mt-1">{detail.dataset.description}</p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-slate-800">{detail.dataset.total_images?.toLocaleString()}</p>
                                        <p className="text-sm text-slate-500">images</p>
                                        <p className="text-lg font-semibold text-slate-600 mt-1">{detail.dataset.total_annotations?.toLocaleString()}</p>
                                        <p className="text-xs text-slate-500">annotations</p>
                                    </div>
                                </div>

                                {/* Status Counts */}
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <h5 className="font-semibold text-slate-700">Image Status Distribution</h5>
                                        <Button size="small" style="secondary" onClick={handleExportDetailStatus}>
                                            <FontAwesomeIcon icon={faDownload} className="mr-1" /> CSV
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {statusNames.map(status => (
                                            <div key={status} className="bg-slate-50 rounded-lg p-4 text-center">
                                                <p className="text-2xl font-bold text-slate-800">
                                                    {(detail.status_counts?.[status] || 0).toLocaleString()}
                                                </p>
                                                <p className="text-sm text-slate-600">{status}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Class Counts */}
                                {Object.keys(detail.class_counts || {}).length > 0 && (
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <h5 className="font-semibold text-slate-700">Annotations by Class</h5>
                                            <Button size="small" style="secondary" onClick={handleExportDetailClasses}>
                                                <FontAwesomeIcon icon={faDownload} className="mr-1" /> CSV
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {Object.entries(detail.class_counts)
                                                .sort(([,a], [,b]) => b - a)
                                                .map(([cls, count]) => (
                                                    <div key={cls} className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                                        <span className="text-sm font-medium text-blue-800">{cls}</span>
                                                        <span className="text-sm font-bold bg-blue-200 text-blue-800 rounded-full px-2 py-0.5">
                                                            {count.toLocaleString()}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* Object Details */}
                                {detail.object_counts && detail.object_counts.length > 0 && (
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <h5 className="font-semibold text-slate-700">
                                                Annotations by Object ({detail.object_counts.length} objects)
                                            </h5>
                                            <Button size="small" style="secondary" onClick={handleExportDetailObjects}>
                                                <FontAwesomeIcon icon={faDownload} className="mr-1" /> CSV
                                            </Button>
                                        </div>
                                        <div className="overflow-x-auto max-h-64 border border-slate-200 rounded-lg">
                                            <table className="w-full">
                                                <thead className="sticky top-0">
                                                    <tr className="bg-slate-100 border-b border-slate-200">
                                                        <th className="text-left px-4 py-2 font-semibold text-slate-600 text-sm">Object</th>
                                                        <th className="text-left px-4 py-2 font-semibold text-slate-600 text-sm">Type</th>
                                                        <th className="text-right px-4 py-2 font-semibold text-slate-600 text-sm">Count</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {detail.object_counts.map((obj, idx) => (
                                                        <tr key={obj.object_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                            <td className="px-4 py-2">
                                                                <span className="font-medium text-slate-800">{obj.object_name}</span>
                                                                <span className="text-xs text-slate-400 ml-2">#{obj.object_id}</span>
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                <span className="text-xs bg-slate-200 text-slate-700 rounded px-2 py-1">
                                                                    {obj.object_type}
                                                                </span>
                                                            </td>
                                                            <td className="text-right px-4 py-2 font-semibold text-slate-700">
                                                                {obj.count.toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {detail.object_counts?.length === 0 && Object.keys(detail.class_counts || {}).length === 0 && (
                                    <div className="text-center py-8 text-slate-500">
                                        <p>No annotations found in this dataset</p>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportingPage;
